import React, { useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react';
import { BoardColumn } from './BoardColumn';
import { Task, BoardData } from './boardTypes';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { BoardContext, type BoardContextValue } from './BoardContext';
import { createRegistry } from './registry';
import { TaskRole } from '../scheduler/schedulerTypes';
import * as boardService from './boardService';

interface TaskBoardProps {
  boardData: BoardData;
  setBoardData: Dispatch<SetStateAction<BoardData>>;
  selectedRole: TaskRole | 'all';
  loading: boolean;
  onEditTask?: (task: Task) => void;
}

// Operation types for tracking what happened after a drag operation
type Operation = {
  trigger: 'pointer' | 'keyboard';
  outcome:
    | {
        type: 'task-reorder';
        columnId: string;
        startIndex: number;
        finishIndex: number;
      }
    | {
        type: 'task-move';
        finishColumnId: string;
        taskIndexInStartColumn: number;
        taskIndexInFinishColumn: number;
      };
};

export function TaskBoard({ boardData, setBoardData, selectedRole, loading, onEditTask }: TaskBoardProps) {
  // Create a stable instance ID to identify this board
  const [instanceId] = useState(() => Symbol('board-instance-id'));
  
  // Create a registry to keep track of columns and tasks
  const [registry] = useState(createRegistry);
  
  // State for tracking the last operation
  const [lastOperation, setLastOperation] = useState<Operation | null>(null);
  
  // Convert board data to the format needed for the component
  const columns = useMemo(() => {
    return boardData.columnOrder.map(columnId => boardData.columns[columnId]);
  }, [boardData]);
  
  // Filter tasks based on selected role
  const getFilteredTasks = useCallback((taskIds: string[]) => {
    if (selectedRole === 'all') {
      return taskIds;
    }
    return taskIds.filter(taskId => {
      const task = boardData.tasks[taskId];
      return task && task.role === selectedRole;
    });
  }, [boardData.tasks, selectedRole]);
  
  // Map column IDs to status values
  const columnToStatus: Record<string, 'planned' | 'in_progress' | 'testing' | 'completed'> = {
    'column-1': 'planned',
    'column-2': 'in_progress',
    'column-3': 'testing',
    'column-4': 'completed'
  };

  // Helper function to calculate index values with spacing
  const calculateIndexForPosition = (
    position: number, 
    taskIds: string[], 
    tasks: { [key: string]: Task }
  ): number => {
    // For the first item, use index = 1000
    if (position === 0) return 1000;
    
    // For the last position, add 1000 to the previous task's index
    if (position === taskIds.length - 1) {
      const prevTaskId = taskIds[position - 1];
      const prevIndex = tasks[prevTaskId]?.index || 0;
      return prevIndex + 1000;
    }
    
    // For middle positions, place halfway between neighbors
    const prevTaskId = taskIds[position - 1];
    const nextTaskId = taskIds[position + 1];
    
    const prevIndex = tasks[prevTaskId]?.index || 0;
    const nextIndex = tasks[nextTaskId]?.index || prevIndex + 2000;
    
    return Math.floor((prevIndex + nextIndex) / 2);
  };

  // Handle task movement and persist position to database
  const handleTaskMoved = async (
    taskId: string,
    sourceColumnId: string,
    destinationColumnId: string,
    destinationIndex: number
  ) => {
    try {
      const statusChanged = sourceColumnId !== destinationColumnId;
      const newStatus = columnToStatus[destinationColumnId as keyof typeof columnToStatus];
      
      // Create a copy of the board data to make updates
      const newBoardData = { ...boardData };
      
      // Get the destination column's taskIds
      const destinationColumn = boardData.columns[destinationColumnId];
      const destinationTaskIds = getFilteredTasks(destinationColumn.taskIds);
      
      // Create an array with the task in its new position
      const updatedTaskIds = [...destinationTaskIds];
      
      // If the task is coming from another column, we need to add it
      if (statusChanged) {
        // Remove from source column
        const sourceColumn = boardData.columns[sourceColumnId];
        newBoardData.columns = {
          ...newBoardData.columns,
          [sourceColumnId]: {
            ...sourceColumn,
            taskIds: sourceColumn.taskIds.filter(id => id !== taskId)
          }
        };
        
        // Add to destination column at the right position
        updatedTaskIds.splice(destinationIndex, 0, taskId);
        newBoardData.columns = {
          ...newBoardData.columns,
          [destinationColumnId]: {
            ...destinationColumn,
            taskIds: [...destinationColumn.taskIds]
          }
        };
        newBoardData.columns[destinationColumnId].taskIds = 
          destinationColumn.taskIds.filter(id => id !== taskId); // Remove if it existed
        
        // Insert at the correct position
        newBoardData.columns[destinationColumnId].taskIds.splice(
          destinationIndex, 
          0, 
          taskId
        );
        
        // Update task status in local state
        newBoardData.tasks = {
          ...newBoardData.tasks,
          [taskId]: {
            ...newBoardData.tasks[taskId],
            status: newStatus
          }
        };
        
        // Calculate new index for the moved task
        const newIndex = calculateIndexForPosition(
          destinationIndex, 
          updatedTaskIds, 
          boardData.tasks
        );
        
        // Update the task's status and index in the database
        await boardService.updateTaskStatusAndIndex(
          taskId, 
          newStatus, 
          newIndex
        );
      } else {
        // For same column reordering, update affected tasks
        // First, find the current index of the task in the array
        const currentIndex = destinationTaskIds.indexOf(taskId);
        
        // Reorder the task IDs in the column
        const reorderedTaskIds = [...destinationColumn.taskIds];
        // Remove from current position
        reorderedTaskIds.splice(
          reorderedTaskIds.indexOf(taskId), 
          1
        );
        // Insert at new position
        reorderedTaskIds.splice(destinationIndex, 0, taskId);
        
        // Update the column in the new board data
        newBoardData.columns = {
          ...newBoardData.columns,
          [destinationColumnId]: {
            ...destinationColumn,
            taskIds: reorderedTaskIds
          }
        };
        
        // Remove from current position for database updates
        updatedTaskIds.splice(currentIndex, 1);
        
        // Insert at new position for database updates
        updatedTaskIds.splice(destinationIndex, 0, taskId);
        
        // Calculate new indexes for affected tasks
        const updates = [];
        
        // Determine the range of affected tasks
        const startIdx = Math.min(currentIndex, destinationIndex);
        const endIdx = Math.max(currentIndex, destinationIndex);
        
        // Update indexes for all affected tasks
        for (let i = startIdx; i <= endIdx; i++) {
          const id = updatedTaskIds[i];
          const newIndex = calculateIndexForPosition(
            i, 
            updatedTaskIds, 
            boardData.tasks
          );
          
          updates.push({
            id,
            status: newStatus,
            index: newIndex
          });
        }
        
        // Batch update all affected tasks
        if (updates.length > 0) {
          await boardService.batchUpdateTaskPositions(updates);
        }
      }
      
      // Update the board data state to reflect changes immediately
      setBoardData(newBoardData);
    } catch (error) {
      console.error("Failed to update task positions:", error);
    }
  };

  // Stable reference to data for use in callbacks
  const stableData = useRef({ columns, tasks: boardData.tasks });
  useEffect(() => {
    stableData.current = { columns, tasks: boardData.tasks };
  }, [columns, boardData.tasks]);

  // Process the result of a drag operation
  useEffect(() => {
    if (!lastOperation) return;
    
    const { outcome, trigger } = lastOperation;
    
    if (outcome.type === 'task-reorder') {
      const { columnId, startIndex, finishIndex } = outcome;
      const column = stableData.current.columns.find(col => col.id === columnId);
      if (!column) return;
      
      const taskIds = getFilteredTasks(column.taskIds);
      const taskId = taskIds[startIndex];
      
      // Trigger a visual flash effect
      const entry = registry.getCard(taskId);
      if (entry?.element) {
        triggerPostMoveFlash(entry.element);
      }
    }
    
    if (outcome.type === 'task-move') {
      const { finishColumnId, taskIndexInFinishColumn } = outcome;
      const destColumn = stableData.current.columns.find(col => col.id === finishColumnId);
      if (!destColumn) return;
      
      const taskIds = getFilteredTasks(destColumn.taskIds);
      const taskId = taskIds[taskIndexInFinishColumn];
      
      // Trigger a visual flash effect
      const entry = registry.getCard(taskId);
      if (entry?.element) {
        triggerPostMoveFlash(entry.element);
      }
    }
  }, [lastOperation, registry, getFilteredTasks]);

  // Monitor for drag and drop operations
  useEffect(() => {
    return combine(
      monitorForElements({
        canMonitor({ source }) {
          return source.data.instanceId === instanceId;
        },
        onDrop(args) {
          const { location, source } = args;
          
          // Didn't drop on anything
          if (!location.current.dropTargets.length) {
            return;
          }
          
          // Handle different types of draggable elements
          if (source.data.type === 'card') {
            const taskId = source.data.itemId;
            if (typeof taskId !== 'string') return;
            
            // Use source.data.sourceColumnId if available (from our enhanced TaskCard data)
            const sourceId = source.data.sourceColumnId || 
              // Fallback to getting it from initial dropTargets
              (() => {
                const [, startColumnRecord] = location.initial.dropTargets;
                return startColumnRecord?.data?.columnId;
              })();
              
            if (typeof sourceId !== 'string') return;
            
            const sourceColumn = stableData.current.columns.find(col => col.id === sourceId);
            if (!sourceColumn) return;
            
            const filteredTaskIds = getFilteredTasks(sourceColumn.taskIds);
            // Use source.data.sourceIndex if available
            const itemIndex = typeof source.data.sourceIndex === 'number' ? 
              source.data.sourceIndex : 
              filteredTaskIds.indexOf(taskId);
            
            // Case 1: Dropped on a column (empty area)
            if (location.current.dropTargets.length === 1) {
              const [destinationColumnRecord] = location.current.dropTargets;
              const destinationId = destinationColumnRecord.data.columnId;
              if (typeof destinationId !== 'string') return;
              
              const destinationColumn = stableData.current.columns.find(col => col.id === destinationId);
              if (!destinationColumn) return;
              
              // Same column reordering
              if (sourceId === destinationId) {
                // Calculate the destination index based on where it was dropped in an empty column
                const destinationIndex = filteredTaskIds.length - 1;
                
                // Call the callback to update the data
                handleTaskMoved(taskId, sourceId, destinationId, destinationIndex);
                
                // Set the last operation for visual feedback
                setLastOperation({
                  trigger: 'pointer',
                  outcome: {
                    type: 'task-reorder',
                    columnId: sourceId,
                    startIndex: itemIndex,
                    finishIndex: destinationIndex,
                  },
                });
                
                return;
              }
              
              // Moving to a new column
              const destinationTaskIds = getFilteredTasks(destinationColumn.taskIds);
              const destinationIndex = destinationTaskIds.length;
              
              // Call the callback to update the data
              handleTaskMoved(taskId, sourceId, destinationId, destinationIndex);
              
              // Set the last operation for visual feedback
              setLastOperation({
                trigger: 'pointer',
                outcome: {
                  type: 'task-move',
                  finishColumnId: destinationId,
                  taskIndexInStartColumn: itemIndex,
                  taskIndexInFinishColumn: destinationIndex,
                },
              });
              
              return;
            }
            
            // Case 2: Dropped on a card in a column
            if (location.current.dropTargets.length === 2) {
              const [destinationCardRecord, destinationColumnRecord] = location.current.dropTargets;
              const destinationColumnId = destinationColumnRecord.data.columnId;
              if (typeof destinationColumnId !== 'string') return;
              
              const destinationColumn = stableData.current.columns.find(col => col.id === destinationColumnId);
              if (!destinationColumn) return;
              
              const destinationTaskIds = getFilteredTasks(destinationColumn.taskIds);
              
              const indexOfTarget = destinationTaskIds.findIndex(
                id => id === destinationCardRecord.data.itemId
              );
              const closestEdgeOfTarget: Edge | null = extractClosestEdge(destinationCardRecord.data);
              
              // Same column reordering
              if (sourceId === destinationColumnId) {
                const destinationIndex = getReorderDestinationIndex({
                  startIndex: itemIndex,
                  indexOfTarget,
                  closestEdgeOfTarget,
                  axis: 'vertical',
                });
                
                // Call the callback to update the data
                handleTaskMoved(taskId, sourceId, destinationColumnId, destinationIndex);
                
                // Set the last operation for visual feedback
                setLastOperation({
                  trigger: 'pointer',
                  outcome: {
                    type: 'task-reorder',
                    columnId: sourceId,
                    startIndex: itemIndex,
                    finishIndex: destinationIndex,
                  },
                });
                
                return;
              }
              
              // Moving to a new column relative to a task
              const destinationIndex = closestEdgeOfTarget === 'bottom' ? indexOfTarget + 1 : indexOfTarget;
              
              // Call the callback to update the data
              handleTaskMoved(taskId, sourceId, destinationColumnId, destinationIndex);
              
              // Set the last operation for visual feedback
              setLastOperation({
                trigger: 'pointer',
                outcome: {
                  type: 'task-move',
                  finishColumnId: destinationColumnId,
                  taskIndexInStartColumn: itemIndex,
                  taskIndexInFinishColumn: destinationIndex,
                },
              });
            }
          }
        },
      })
    );
  }, [instanceId, getFilteredTasks, handleTaskMoved]);

  // Helper function to get columns data
  const getColumnsData = useCallback(() => {
    return stableData.current.columns;
  }, []);

  // Reorder a task within a column
  const reorderTask = useCallback(({
    columnId,
    startIndex,
    finishIndex,
    trigger = 'keyboard',
  }: {
    columnId: string;
    startIndex: number;
    finishIndex: number;
    trigger?: 'pointer' | 'keyboard';
  }) => {
    const column = stableData.current.columns.find(col => col.id === columnId);
    if (!column) return;
    
    const taskIds = getFilteredTasks(column.taskIds);
    const taskId = taskIds[startIndex];
    
    // Call the handler to update the data
    handleTaskMoved(taskId, columnId, columnId, finishIndex);
    
    // Set the last operation for visual feedback
    setLastOperation({
      trigger,
      outcome: {
        type: 'task-reorder',
        columnId,
        startIndex,
        finishIndex,
      },
    });
  }, [getFilteredTasks, handleTaskMoved]);

  // Move a task between columns
  const moveTask = useCallback(({
    startColumnId,
    finishColumnId,
    taskIndexInStartColumn,
    taskIndexInFinishColumn,
    trigger = 'keyboard',
  }: {
    startColumnId: string;
    finishColumnId: string;
    taskIndexInStartColumn: number;
    taskIndexInFinishColumn?: number;
    trigger?: 'pointer' | 'keyboard';
  }) => {
    // Invalid cross-column movement
    if (startColumnId === finishColumnId) {
      return;
    }
    
    const sourceColumn = stableData.current.columns.find(col => col.id === startColumnId);
    const destinationColumn = stableData.current.columns.find(col => col.id === finishColumnId);
    
    if (!sourceColumn || !destinationColumn) return;
    
    const sourceTaskIds = getFilteredTasks(sourceColumn.taskIds);
    const destinationTaskIds = getFilteredTasks(destinationColumn.taskIds);
    
    const taskId = sourceTaskIds[taskIndexInStartColumn];
    
    // Going into the first position if no index is provided
    const newIndexInDestination = taskIndexInFinishColumn ?? destinationTaskIds.length;
    
    // Call the handler to update the data
    handleTaskMoved(taskId, startColumnId, finishColumnId, newIndexInDestination);
    
    // Set the last operation for visual feedback
    setLastOperation({
      trigger,
      outcome: {
        type: 'task-move',
        finishColumnId,
        taskIndexInStartColumn,
        taskIndexInFinishColumn: newIndexInDestination,
      },
    });
  }, [getFilteredTasks, handleTaskMoved]);

  // Create the board context value
  const contextValue: BoardContextValue = useMemo(() => {
    return {
      getColumns: getColumnsData,
      reorderTask,
      moveTask,
      registerCard: registry.registerCard,
      registerColumn: registry.registerColumn,
      instanceId,
    };
  }, [getColumnsData, reorderTask, moveTask, registry, instanceId]);

  if (loading) {
    return null;
  }

  return (
    <BoardContext.Provider value={contextValue}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 h-full">
        {columns.map((column, index) => (
          <BoardColumn
            key={column.id}
            column={column}
            tasks={boardData.tasks}
            index={index}
            getFilteredTasks={getFilteredTasks}
            onEditTask={onEditTask}
          />
        ))}
      </div>
    </BoardContext.Provider>
  );
}
